import React from 'react'
import { DateTime } from 'luxon'
import { SafeParseReturnType, z } from 'zod'
import { Offer } from '@/services/PifService'
import { GuildSale } from '@/services/SaleService'
import { Result } from '@/utils/types'
import { GuildStatus, getEmptyGuildStatus } from '@/utils/users/usersServer'
import { BASIC_TIER } from './tiers'

export interface State {
  locale: string
  // NOTE - for guild checkout context
  shouldShowAvailabilityWarning: boolean
  offer: Offer
  sale: GuildSale | null
  guildStatus: GuildStatus
  screen: GuildGiftScreen
  invitation: {
    name: string
    email: string
    message: string
    scheduleMode: 'immediately' | 'future'
    scheduleAt: string
    /**
     * Supports the display of validation errors only after submit. Thereafter,
     * validations will display updates inline as typing.
     */
    isSubmitted: boolean
    /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
    validation?: SafeParseReturnType<any, any>
    result?: Result<InvitationCreatedResponse>
  }
  tier: {
    name: string
    offerId: string
  }
}

export type Action = TransitionScreenAction | AssocInvitationAction | InvitationSubmitAction | InvitationCreatedAction

interface TransitionScreenAction {
  type: 'transition'
  screen: GuildGiftScreen
}

interface AssocInvitationAction {
  type: 'assoc-invitation'
  name: string
  value: string
}

interface InvitationSubmitAction {
  type: 'invitation-submit'
}

interface InvitationCreatedAction {
  type: 'invitation-created'
  result: Result<InvitationCreatedResponse>
}

export interface InvitationCreatedResponse {
  id: string
  state: string
}

export interface InvitationUpdatedResponse {
  id: string
  state: string
}

export const SCREENS = ['invite', 'payment', 'success'] as const
export type GuildGiftScreen = (typeof SCREENS)[number]
export const MAX_MESSAGE_CHARS_LENGTH = 200

export function ensureScreen(maybeScreen: unknown) {
  return isScreen(maybeScreen) ? maybeScreen : DEFAULT_STATE.screen

  function isScreen(str: unknown): str is GuildGiftScreen {
    return typeof str === 'string' && (SCREENS as readonly string[]).includes(str)
  }
}

export const DEFAULT_STATE = {
  locale: 'en',
  shouldShowAvailabilityWarning: false,
  offer: {
    id: 'defaultOfferId',
    enabled: false,
    currency: 'USD',
    region: 'US',
    name: 'Default Offer',
    price: 0,
    slug: 'default-offer-slug',
    offeredproductSet: [],
  },
  sale: null,
  guildStatus: getEmptyGuildStatus(),
  screen: 'invite' as const,
  invitation: {
    name: '',
    email: '',
    message: 'Default message',
    scheduleMode: 'immediately' as const,
    scheduleAt: today(),
    isSubmitted: false,
  },
  tier: BASIC_TIER,
}

export function reducer(state: State, action: Action): State {
  switch (action.type) {
    case 'transition':
      return reduceTransition(state, action)
    case 'assoc-invitation':
      return reduceAssocInvitation(state, action)
    case 'invitation-submit':
      return reduceInvitationSubmit(state)
    case 'invitation-created':
      return reduceInvitationCreated(state, action)
  }

  function reduceInvitationSubmit(state: State) {
    return {
      ...state,
      invitation: {
        ...state.invitation,
        isSubmitted: true,
      },
    }
  }

  function reduceInvitationCreated(state: State, action: InvitationCreatedAction) {
    return {
      ...state,
      invitation: {
        ...state.invitation,
        result: action.result,
      },
    }
  }

  function reduceTransition(state: State, action: TransitionScreenAction) {
    const isInviteSubmit = state.screen === 'invite' && action.screen === 'payment'
    const invitationValidation = isInviteSubmit ? InvitationSchema.safeParse(state.invitation) : undefined
    const invitationCreatedError = state.invitation.result?.ok === false ? state.invitation.result.error : undefined
    return {
      ...state,
      invitation: isInviteSubmit
        ? {
            ...state.invitation,
            isSubmitted: true,
            validation: InvitationSchema.safeParse(state.invitation),
          }
        : state.invitation,
      screen: invitationValidation?.error || invitationCreatedError ? state.screen : action.screen,
    }
  }

  function reduceAssocInvitation(state: State, action: AssocInvitationAction) {
    const newInvitation = {
      ...state.invitation,
      scheduleAt:
        action.name === 'scheduleMode' && action.value === 'immediately' ? today() : state.invitation.scheduleAt,
      [action.name]: action.value,
    }
    return {
      ...state,
      invitation: {
        ...newInvitation,
        validation: InvitationSchema.safeParse(newInvitation),
      },
    }
  }
}

export const GuildGiftContext = React.createContext<{
  state: State
  dispatch: React.Dispatch<Action>
}>({
  state: DEFAULT_STATE,
  dispatch: () => {},
})

const InvitationFields = z.object({
  name: z.string().nonempty({ message: 'Recipient name required' }),
  email: z.string().nonempty({ message: 'Recipient email required' }).email({ message: 'Invalid recipient email' }),
  message: z.string().max(MAX_MESSAGE_CHARS_LENGTH),
  scheduleMode: z.enum(['immediately', 'future']),
  scheduleAt: z.string(),
})
type Invitation = z.infer<typeof InvitationFields>
export const InvitationSchema = InvitationFields.superRefine(validateScheduleAtDate)

function validateScheduleAtDate(val: Invitation, ctx: z.RefinementCtx) {
  if (val.scheduleMode === 'future') {
    const d = new Date(val.scheduleAt)
    if (isNaN(d.getTime())) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['scheduleAt'],
        message: 'Invalid date',
      })
      return
    }
  }

  if (val.scheduleAt && val.scheduleAt.trim() !== '') {
    const d = DateTime.fromISO(val.scheduleAt).startOf('day').toJSDate()
    if (isNaN(d.getTime())) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['scheduleAt'],
        message: 'Invalid date',
      })
      return
    }
    const min = DateTime.now().startOf('day').toJSDate()
    const max = DateTime.fromJSDate(min).plus({ month: 3 }).toJSDate()

    if (d < min) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['scheduleAt'],
        message: 'Date must be in the future',
      })
    }

    if (d > max) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['scheduleAt'],
        message: 'Date must be within 3 months from today',
      })
    }
  }
}

export function today(): string {
  return DateTime.now().toFormat('yyyy-MM-dd')
}

/**
 * The user can only select the day to send. But we need an exact time.
 * If the scheduleMode is immediate, use *now* as the time to append to the date.
 * If other, use a standard time of 13:00 UTC, which is either 6am MST or 7am MDT.
 */
export function formatSendAt(state: State) {
  return state.invitation.scheduleMode === 'immediately'
    ? new Date().toISOString()
    : state.invitation.scheduleAt + 'T13:00:00.000Z'
}
